/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import './StudentExamTaking.css';
import { apiFetch, resolveMediaUrl } from './api';
import MathText from './MathText';
import logo from './logo.png';

function StudentExamTaking({ exam, studentId, attemptId, user, onComplete }) {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({}); // MultipleChoice için: {attempt_question_id: 'A'}, Classic için: {attempt_question_id: 'cevap metni'}
  const [classicAnswers, setClassicAnswers] = useState({}); // Klasik sorular için metin cevapları
  const [skippedQuestions, setSkippedQuestions] = useState(new Set()); // Geçilen sorular
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [remainingTime, setRemainingTime] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeExpired, setTimeExpired] = useState(false);
  const [autoSave, setAutoSave] = useState(true); // Otomatik kaydetme ayarı
  const [finishConfirmation, setFinishConfirmation] = useState('');
  const [currentPage, setCurrentPage] = useState(null); // null, 'finish-confirmation', 'submit-success'
  const [submitResult, setSubmitResult] = useState(null); // {total_points}
  const [pendingSkipQuestionId, setPendingSkipQuestionId] = useState(null); // Boş geçilmek istenen soru ID'si
  const [violationModal, setViolationModal] = useState(null); // {count, max} — uyarı popup
  const [autoSubmitModal, setAutoSubmitModal] = useState(null); // {message} — otomatik teslim popup
  const [examLocked, setExamLocked] = useState(false); // 2. monitör ihlalinde input'ları kilitle
  // Mobil klavye açıldığında bazı tarayıcılar 100dvh'i güncellemez. Görünen
  // viewport yüksekliğini takip ederek alt navigasyonun klavyenin altında
  // kalmasını engelliyoruz.
  const [viewportHeight, setViewportHeight] = useState(null);
  const timerRef = useRef(null);
  const timeSyncRef = useRef(null); // Süre senkronizasyonu için interval
  const fetchingRef = useRef(false);
  const authErrorShownRef = useRef(false); // 403 mesajını tek sefer göster
  const lastViewedQuestionIndexRef = useRef(null); // Son görüntülenen soru index'i
  const violationCountRef = useRef(0);      // Toplam ihlal sayısı
  const lastViolationTimeRef = useRef(0);   // Çift-tetiklemeyi önlemek için (blur + visibilitychange)
  const fullscreenDeniedRef = useRef(false); // Fullscreen reddedildi mi
  const fullscreenTransitionRef = useRef(false); // Fullscreen geçişi sırasında ihlali bastır
  const pageUnloadingRef = useRef(false);   // Sayfa yenileme/kapatma sırasında ihlali bastır
  const classicSaveTimersRef = useRef({});
  const pendingClassicAnswersRef = useRef({});
  const initialScreenXRef = useRef(null);   // Pencere başlangıç X konumu (2. monitör tespiti)
  const initialScreenYRef = useRef(null);   // Pencere başlangıç Y konumu
  const screenPollRef = useRef(null);        // Pencere konum polling interval'ı

  // Soru işaretleme state
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());

  // Saat/tarih state
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  // Saat/tarih güncelleme
  useEffect(() => {
    const clockInterval = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);
    return () => clearInterval(clockInterval);
  }, []);

  useEffect(() => {
    const viewport = window.visualViewport;
    const updateViewportHeight = () => {
      setViewportHeight(Math.round(viewport?.height || window.innerHeight));
    };

    updateViewportHeight();
    viewport?.addEventListener('resize', updateViewportHeight);
    window.addEventListener('resize', updateViewportHeight);

    return () => {
      viewport?.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('resize', updateViewportHeight);
    };
  }, []);

  useEffect(() => {
    // Çift çağrıyı önle (React strict mode için)
    if (fetchingRef.current) {
      return;
    }
    fetchingRef.current = true;
    
    fetchQuestions().finally(() => {
      fetchingRef.current = false;
    });
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (timeSyncRef.current) {
        clearInterval(timeSyncRef.current);
      }
      if (screenPollRef.current) {
        clearInterval(screenPollRef.current);
      }
      Object.values(classicSaveTimersRef.current).forEach(clearTimeout);
    };
  }, []);

  // Soru değiştiğinde pending skip'i temizle ve son görüntülenen soru index'ini kaydet
  useEffect(() => {
    setPendingSkipQuestionId(null);
    // Son görüntülenen soru index'ini kaydet
    if (questions.length > 0 && currentQuestionIndex >= 0) {
      const localStorageKey = `exam_${exam.exam_id}_attempt_${attemptId}_lastQuestionIndex`;
      localStorage.setItem(localStorageKey, currentQuestionIndex.toString());
      lastViewedQuestionIndexRef.current = currentQuestionIndex;
    }
  }, [currentQuestionIndex, exam.exam_id, attemptId, questions.length]);

  // Sekme/pencere değiştirme ihlali takibi
  // Fullscreen yönetimi — sorular yüklenince tam ekran iste
  useEffect(() => {
    if (loading || questions.length === 0) return;

    const requestFs = async () => {
      // Fullscreen geçişi sırasında blur/visibilitychange ihlallerini bastır
      fullscreenTransitionRef.current = true;
      try {
        const el = document.documentElement;
        if (el.requestFullscreen) await el.requestFullscreen();
        else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
        else if (el.mozRequestFullScreen) await el.mozRequestFullScreen();
      } catch (err) {
        // Kullanıcı reddetti veya tarayıcı desteklemiyor — ihlal olarak say
        fullscreenDeniedRef.current = true;
        console.warn('Fullscreen reddedildi:', err);
      } finally {
        // Fullscreen geçişi tamamlandı, ihlal tespitini tekrar aç (1sn gecikme ile)
        setTimeout(() => { fullscreenTransitionRef.current = false; }, 1000);
      }
    };

    requestFs();

    const handleFsChange = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement;
      if (!fsEl) {
        // Fullscreen'den çıkıldı — ihlal tetikle (sadece sınav aktifse)
        if (!isSubmittingRef.current && !timeExpiredRef.current) {
          // Küçük gecikme: tarayıcı focus olaylarının ardından gelsin
          setTimeout(() => {
            if (!document.fullscreenElement && !isSubmittingRef.current && !timeExpiredRef.current) {
              lastViolationTimeRef.current = 0; // debounce'u sıfırla, kesin tetiklensin
              // handleViolation doğrudan çağrılamaz (closure dışında), event dispatch kullan
              window.dispatchEvent(new Event('exam-fullscreen-exit'));
            }
          }, 300);
        }
      } else {
        // Tam ekrana geri dönüldü — kilidi aç
        setExamLocked(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFsChange);
    document.addEventListener('webkitfullscreenchange', handleFsChange);
    document.addEventListener('mozfullscreenchange', handleFsChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFsChange);
      document.removeEventListener('webkitfullscreenchange', handleFsChange);
      document.removeEventListener('mozfullscreenchange', handleFsChange);
      // Component unmount olduğunda fullscreen'den çık
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, [loading, questions.length]);

  // Stale closure'dan kaçınmak için isSubmitting/timeExpired/violationModal ref üzerinden okunuyor
  const isSubmittingRef = useRef(false);
  const timeExpiredRef = useRef(false);
  const violationModalOpenRef = useRef(false);

  useEffect(() => { isSubmittingRef.current = isSubmitting; }, [isSubmitting]);
  useEffect(() => { timeExpiredRef.current = timeExpired; }, [timeExpired]);
  useEffect(() => { violationModalOpenRef.current = !!violationModal; }, [violationModal]);

  // 2. monitör ihlali için custom event dispatch — closure dışında tutulur
  const triggerDualMonitorViolation = () => {
    window.dispatchEvent(new Event('exam-dual-monitor'));
  };

  useEffect(() => {
    // Sorular yüklenene kadar ihlal tespitini başlatma
    if (loading || questions.length === 0) return;

    const MAX_VIOLATIONS = 2;
    const DEBOUNCE_MS = 800;

    const handleViolation = async () => {
      // Zaten teslim ediliyorsa, süre dolmuşsa, uyarı modalı açıksa, fullscreen geçişindeyse veya sayfa yenileniyorsa işlem yapma
      if (isSubmittingRef.current || timeExpiredRef.current || violationModalOpenRef.current || fullscreenTransitionRef.current || pageUnloadingRef.current) return;

      const now = Date.now();
      if (now - lastViolationTimeRef.current < DEBOUNCE_MS) return;
      lastViolationTimeRef.current = now;

      violationCountRef.current += 1;
      const currentCount = violationCountRef.current;

      // Backend'e ihlali kaydet (fire-and-forget)
      try {
        await apiFetch(`/student/exams/${exam.exam_id}/violation`, {
          method: 'POST',
          body: JSON.stringify({
            attempt_id: attemptId,
            violation_count: currentCount
          })
        });
      } catch (err) {
        console.error('İhlal kaydedilemedi:', err);
      }

      if (currentCount >= MAX_VIOLATIONS) {
        await new Promise(resolve => setTimeout(resolve, 500));
        handleAutoSubmit();
      } else {
        setViolationModal({ count: currentCount, max: MAX_VIOLATIONS });
      }
    };

    // blur + visibilitychange aynı sekme değiştirmede ikisi birden sayılmasın.
    // Strateji: blur gelince kısa bekle; eğer visibilitychange de gelirse blur'u iptal et.
    let blurTimer = null;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (blurTimer) {
          clearTimeout(blurTimer);
          blurTimer = null;
        }
        // Küçük gecikme: beforeunload / Ctrl+R pageUnloadingRef'i önceden set etmiş olabilir
        setTimeout(() => {
          if (!pageUnloadingRef.current) {
            handleViolation();
          }
        }, 50);
      }
    };

    const handleWindowBlur = () => {
      // visibilitychange önce gelecekse blur'u beklet
      blurTimer = setTimeout(() => {
        blurTimer = null;
        if (!pageUnloadingRef.current) {
          handleViolation();
        }
      }, 200);
    };

    const handleFullscreenExit = () => {
      handleViolation();
    };

    const handleDualMonitorViolation = () => {
      if (fullscreenTransitionRef.current) return;
      handleViolation();
      setExamLocked(true);
    };

    // Sayfa yenileme/kapatma öncesinde ihlal tetiklenmesini bastır
    const handleBeforeUnload = () => {
      pageUnloadingRef.current = true;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('exam-fullscreen-exit', handleFullscreenExit);
    window.addEventListener('exam-dual-monitor', handleDualMonitorViolation);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      if (blurTimer) clearTimeout(blurTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('exam-fullscreen-exit', handleFullscreenExit);
      window.removeEventListener('exam-dual-monitor', handleDualMonitorViolation);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [exam.exam_id, attemptId, loading, questions.length]); // sorular yüklenince bağlan

  // F1–F12, Tab ve tehlikeli Ctrl/Alt kombinasyonlarını engelle
  useEffect(() => {
    const handleKeyDown = (e) => {
      // F1–F12
      if (e.key >= 'F1' && e.key <= 'F12') {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // AltGr = Ctrl+Alt — Türkçe klavyede özel karakter (@, €, vb.) için gerekli, engelleme
      if (e.ctrlKey && e.altKey) return;
      // Ctrl/Meta kombinasyonları: tarayıcı sekmesi, pencere, geçmiş, kaynak, yenileme vb.
      if (e.ctrlKey || e.metaKey) {
        // Ctrl+R / Cmd+R: sayfa yenilemeden önce ihlal bastır, sonra engelle
        if (e.key.toLowerCase() === 'r') {
          pageUnloadingRef.current = true;
          e.preventDefault();
          e.stopPropagation();
          setTimeout(() => { pageUnloadingRef.current = false; }, 1500);
          return;
        }
        const blocked = ['t', 'n', 'w', 'l', 'h', 'j', 'u', 'p'];
        if (blocked.includes(e.key.toLowerCase())) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
      // Alt kombinasyonları (AltGr hariç): geri/ileri navigasyon, adres çubuğu vb.
      if (e.altKey && !e.ctrlKey) {
        const blocked = ['ArrowLeft', 'ArrowRight', 'd', 'D'];
        if (blocked.includes(e.key)) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);

  // 2. monitör tespiti: mouse ekran dışına çıkınca veya pencere başka monitöre taşınınca ihlal tetikle
  useEffect(() => {
    if (loading || questions.length === 0) return;

    // İlk mouse hareketi ile pencere konumu baseline'ını al (false positive önlemek için)
    const captureBaseline = () => {
      if (initialScreenXRef.current === null) {
        initialScreenXRef.current = window.screenX;
        initialScreenYRef.current = window.screenY;
      }
    };
    document.addEventListener('mousemove', captureBaseline, { once: true });

    // Mouse tarayıcı viewport'unu terk edince ihlal tetikle
    const handleMouseLeave = (e) => {
      if (e.relatedTarget === null) {
        if (!isSubmittingRef.current && !timeExpiredRef.current) {
          triggerDualMonitorViolation();
        }
      }
    };
    document.addEventListener('mouseleave', handleMouseLeave);

    // Pencere 2. monitöre taşınınca ihlal tetikle (500ms polling)
    const POSITION_THRESHOLD = 600; // piksel — iki monitör arası geçişi ayırt eder
    screenPollRef.current = setInterval(() => {
      if (isSubmittingRef.current || timeExpiredRef.current) return;
      if (initialScreenXRef.current === null) return;
      const dX = Math.abs(window.screenX - initialScreenXRef.current);
      const dY = Math.abs(window.screenY - initialScreenYRef.current);
      if (dX > POSITION_THRESHOLD || dY > POSITION_THRESHOLD) {
        // Yeni pozisyonu baseline olarak güncelle (tekrar tetiklenmesin)
        initialScreenXRef.current = window.screenX;
        initialScreenYRef.current = window.screenY;
        triggerDualMonitorViolation();
      }
    }, 500);

    return () => {
      document.removeEventListener('mousemove', captureBaseline);
      document.removeEventListener('mouseleave', handleMouseLeave);
      if (screenPollRef.current) clearInterval(screenPollRef.current);
    };
  }, [loading, questions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await apiFetch(
        `/student/exams/${exam.exam_id}/questions?attempt_id=${attemptId}`
      );
      if (response.status === 403) {
        const authMessage = 'Sınava erişim izniniz yok. Lütfen öğrenci hesabınızla yeniden giriş yapın.';
        setError(authMessage);
        if (timeSyncRef.current) {
          clearInterval(timeSyncRef.current);
          timeSyncRef.current = null;
        }
        return;
      }
      const data = await response.json();

      if (data.success) {
        setQuestions(data.questions || []);
        
        // Geçilen soruları ve mevcut cevapları yükle
        const skipped = new Set();
        const loadedAnswers = {};
        const loadedClassicAnswers = {};
        (data.questions || []).forEach(q => {
          if (q.is_skipped) {
            skipped.add(q.attempt_question_id);
          }
          // Mevcut cevapları yükle
          if (q.student_answer) {
            if (q.question_type === 'Classic' && q.student_answer.classic_answer_text) {
              loadedClassicAnswers[q.attempt_question_id] = q.student_answer.classic_answer_text;
            } else if (q.question_type === 'MultipleChoice' && q.student_answer.selected_choice_index !== null && q.student_answer.selected_choice_index !== undefined) {
              loadedAnswers[q.attempt_question_id] = String.fromCharCode(65 + q.student_answer.selected_choice_index); // 0->A, 1->B, etc.
            }
          }
        });
        setSkippedQuestions(skipped);
        setAnswers(loadedAnswers);
        setClassicAnswers(loadedClassicAnswers);
        
        // Kaldığı yerden devam et
        // Önce localStorage'dan son görüntülenen soru index'ini kontrol et
        const localStorageKey = `exam_${exam.exam_id}_attempt_${attemptId}_lastQuestionIndex`;
        const savedLastQuestionIndex = localStorage.getItem(localStorageKey);
        
        let resumeIndex = data.resume_question_index !== undefined 
          ? data.resume_question_index 
          : 0;
        
        // Eğer localStorage'da kayıtlı bir index varsa ve o soru hala geçerliyse, her zaman o soruya dön
        // Cevap verilmiş olsa bile, öğrenci "Sonraki" butonuna basmadan çıktıysa aynı soruda kalmalı
        if (savedLastQuestionIndex !== null) {
          const savedIndex = parseInt(savedLastQuestionIndex, 10);
          if (!isNaN(savedIndex) && savedIndex >= 0 && savedIndex < (data.questions || []).length) {
            const savedQuestion = (data.questions || [])[savedIndex];
            const savedQuestionId = savedQuestion?.attempt_question_id;
            
            // Eğer kayıtlı soru skip edilmemişse ve hala mevcutsa, her zaman o soruya dön
            // Cevap verilmiş olup olmadığına bakma - öğrenci kaldığı sorudan devam etmeli
            if (savedQuestionId && !skipped.has(savedQuestionId)) {
              resumeIndex = savedIndex;
            }
          }
        }
        
        setCurrentQuestionIndex(resumeIndex);
        lastViewedQuestionIndexRef.current = resumeIndex;
        
        // Backend'deki ihlal sayısını ref'e yükle (sayfa yenilemede sıfırlanmasın)
        if (data.violation_count !== undefined && data.violation_count !== null) {
          violationCountRef.current = data.violation_count;
        }

        // Otomatik kaydetme her zaman aktif
        if (data.finish_confirmation) {
          setFinishConfirmation(data.finish_confirmation);
        }
        if (data.remaining_seconds !== null && data.remaining_seconds !== undefined) {
          setRemainingTime(data.remaining_seconds);
          startTimer(data.remaining_seconds);
        } else if (data.time_limit_at) {
          // Fallback: remaining_seconds null gelirse time_limit_at'ten hesapla
          const serverNow = data.server_time ? new Date(data.server_time) : new Date();
          const timeLimitAt = new Date(data.time_limit_at);
          const remainingSecs = Math.max(0, Math.floor((timeLimitAt - serverNow) / 1000));
          if (remainingSecs > 0) {
            setRemainingTime(remainingSecs);
            startTimer(remainingSecs);
          }
        }
      } else {
        if (data.time_expired) {
          setTimeExpired(true);
          setError('Süre doldu, sınav otomatik olarak teslim edildi');
          setTimeout(() => {
            onComplete();
          }, 2000);
        } else {
          setError(data.message || 'Sorular yüklenemedi');
        }
      }
    } catch (err) {
      setError('Bağlantı hatası');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const startTimer = (initialSeconds) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    let seconds = initialSeconds;
    setRemainingTime(seconds);

    timerRef.current = setInterval(() => {
      seconds -= 1;
      setRemainingTime(seconds);

      if (seconds <= 0) {
        clearInterval(timerRef.current);
        setTimeExpired(true);
        handleAutoSubmit();
      }
    }, 1000);
    
    // Periyodik olarak backend'den güncel süreyi çek (30 saniyede bir)
    if (timeSyncRef.current) {
      clearInterval(timeSyncRef.current);
    }
    
    timeSyncRef.current = setInterval(async () => {
      try {
        const response = await apiFetch(
          `/student/exams/${exam.exam_id}/questions?attempt_id=${attemptId}`
        );
        if (response.status === 403) {
          if (timeSyncRef.current) {
            clearInterval(timeSyncRef.current);
            timeSyncRef.current = null;
          }
          if (!authErrorShownRef.current) {
            authErrorShownRef.current = true;
            setError('Oturum yetkisi doğrulanamadı. Lütfen öğrenci hesabınızla yeniden giriş yapın.');
          }
          return;
        }
        const data = await response.json();
        
        if (data.success && data.remaining_seconds !== null && data.remaining_seconds !== undefined) {
          // Backend'den gelen süre ile local süre arasındaki fark 5 saniyeden fazlaysa güncelle
          const serverTime = data.remaining_seconds;
          const currentLocalTime = remainingTime ?? 0;
          const diff = Math.abs(currentLocalTime - serverTime);
          
          if (diff > 5) {
            // Timer'ı yeniden başlat
            startTimer(serverTime);
          }
        }
      } catch (err) {
        console.error('Süre senkronizasyon hatası:', err);
      }
    }, 10000); // 10 saniyede bir kontrol et (daha sık kontrol)
  };

  const maybeResetTimer = (serverRemaining) => {
    if (serverRemaining === null || serverRemaining === undefined) return;
    const current = remainingTime ?? 0;
    const diff = Math.abs(current - serverRemaining);
    if (diff > 5) {
      startTimer(serverRemaining);
    }
  };

  const handleAnswerChange = async (attemptQuestionId, selectedAnswer) => {
    // SKIP değeri geldiğinde skip endpoint'ini kullan
    if (selectedAnswer === 'SKIP') {
      // Local state'i güncelle
      setAnswers((prev) => ({
        ...prev,
        [attemptQuestionId]: 'SKIP'
      }));
      
      // Geçilen sorular listesine ekle
      setSkippedQuestions((prev) => {
        const newSet = new Set(prev);
        newSet.add(attemptQuestionId);
        return newSet;
      });

      // Skip endpoint'ini çağır
      try {
        const response = await apiFetch(
          `/student/exams/${exam.exam_id}/questions/${attemptQuestionId}/skip`,
          {
            method: 'POST',
            body: JSON.stringify({
              attempt_id: attemptId
            })
          }
        );

        const data = await response.json();
        if (!data.success) {
          console.error('Soru geçilemedi:', data.message);
        } else {
          maybeResetTimer(data.remaining_seconds);
        }
      } catch (err) {
        console.error('Soru geçme hatası:', err);
      }
      return;
    }

    // Normal cevap değişikliği (A, B, C, D, E)
    // Eğer aynı şık tekrar seçilirse, seçimi kaldır (toggle)
    const currentAnswer = answers[attemptQuestionId];
    const newAnswer = currentAnswer === selectedAnswer ? null : selectedAnswer;
    
    // Önce local state'i güncelle
    setAnswers((prev) => {
      if (newAnswer === null) {
        const updated = { ...prev };
        delete updated[attemptQuestionId];
        return updated;
      }
      return {
        ...prev,
        [attemptQuestionId]: newAnswer
      };
    });

    // Seçimi kaldırmak "geç" işlemi değildir; soru normal cevapsız kalır.
    setSkippedQuestions((prev) => {
      const newSet = new Set(prev);
      newSet.delete(attemptQuestionId);
      return newSet;
    });

    // Her zaman otomatik kaydet
    try {
      const response = await apiFetch(
        `/student/exams/${exam.exam_id}/answers`,
        {
          method: 'POST',
          body: JSON.stringify({
            attempt_id: attemptId,
            attempt_question_id: attemptQuestionId,
            selected_answer: newAnswer
          })
        }
      );

      const data = await response.json();
      if (!data.success) {
        console.error('Cevap kaydedilemedi:', data.message);
      } else {
        maybeResetTimer(data.remaining_seconds);
      }
    } catch (err) {
      console.error('Cevap kaydetme hatası:', err);
    }
  };

  const saveClassicAnswer = async (attemptQuestionId, answerText) => {
    try {
      const response = await apiFetch(
        `/student/exams/${exam.exam_id}/answers`,
        {
          method: 'POST',
          body: JSON.stringify({
            attempt_id: attemptId,
            attempt_question_id: attemptQuestionId,
            classic_answer_text: answerText
          })
        }
      );
      const data = await response.json();
      if (!data.success) console.error('Cevap kaydedilemedi:', data.message);
      else maybeResetTimer(data.remaining_seconds);
    } catch (err) {
      console.error('Cevap kaydetme hatası:', err);
    }
  };

  const flushClassicAnswer = async (attemptQuestionId) => {
    const timer = classicSaveTimersRef.current[attemptQuestionId];
    if (timer) clearTimeout(timer);
    delete classicSaveTimersRef.current[attemptQuestionId];
    if (!Object.prototype.hasOwnProperty.call(pendingClassicAnswersRef.current, attemptQuestionId)) return;
    const answerText = pendingClassicAnswersRef.current[attemptQuestionId];
    delete pendingClassicAnswersRef.current[attemptQuestionId];
    await saveClassicAnswer(attemptQuestionId, answerText);
  };

  const flushAllClassicAnswers = async () => {
    const ids = Object.keys(pendingClassicAnswersRef.current);
    await Promise.all(ids.map((id) => flushClassicAnswer(id)));
  };

  const handleClassicAnswerChange = (attemptQuestionId, answerText) => {
    // Önce local state'i güncelle
    setClassicAnswers((prev) => ({
      ...prev,
      [attemptQuestionId]: answerText
    }));

    // Geçilen sorular listesinden çıkar (cevap verildi)
    setSkippedQuestions((prev) => {
      const newSet = new Set(prev);
      newSet.delete(attemptQuestionId);
      return newSet;
    });

    // Her tuşta istek göndermek yerine yazma durduktan 700 ms sonra kaydet.
    pendingClassicAnswersRef.current[attemptQuestionId] = answerText;
    if (classicSaveTimersRef.current[attemptQuestionId]) {
      clearTimeout(classicSaveTimersRef.current[attemptQuestionId]);
    }
    classicSaveTimersRef.current[attemptQuestionId] = setTimeout(
      () => flushClassicAnswer(attemptQuestionId),
      700
    );
  };

  const handleSkipQuestion = async (attemptQuestionId) => {
    // Soru zaten cevaplanmışsa geçilemez
    const currentQuestion = questions[currentQuestionIndex];
    const isAnswered = currentQuestion.question_type === 'Classic' 
      ? (classicAnswers[attemptQuestionId] || '').trim()
      : answers[attemptQuestionId];
    
    if (isAnswered) {
      return;
    }

    // Soru zaten geçilmişse tekrar geçilemez
    if (skippedQuestions.has(attemptQuestionId)) {
      return;
    }

    try {
      const response = await apiFetch(
        `/student/exams/${exam.exam_id}/questions/${attemptQuestionId}/skip`,
        {
          method: 'POST',
          body: JSON.stringify({
            attempt_id: attemptId
          })
        }
      );

      const data = await response.json();
      if (data.success) {
        // Geçilen sorular listesine ekle
        setSkippedQuestions((prev) => new Set(prev).add(attemptQuestionId));
        
        // Sonraki geçilmemiş soruya geç
        let foundIndex = -1;
        for (let i = currentQuestionIndex + 1; i < questions.length; i++) {
          const nextQuestionId = questions[i].attempt_question_id;
          if (!skippedQuestions.has(nextQuestionId)) {
            foundIndex = i;
            break;
          }
        }
        
        if (foundIndex !== -1) {
          setCurrentQuestionIndex(foundIndex);
        } else {
          // Geçilebilecek başka soru yok, son soruya geç
          if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(questions.length - 1);
          }
        }
        maybeResetTimer(data.remaining_seconds);
      } else {
        alert(data.message || 'Soru geçilemedi');
      }
    } catch (err) {
      console.error('Soru geçme hatası:', err);
      alert('Soru geçilirken hata oluştu');
    }
  };

  const handleFlagQuestion = (attempt_question_id) => {
    setFlaggedQuestions(prev => {
      const next = new Set(prev);
      if (next.has(attempt_question_id)) next.delete(attempt_question_id);
      else next.add(attempt_question_id);
      return next;
    });
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      const newIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(newIndex);
      const localStorageKey = `exam_${exam.exam_id}_attempt_${attemptId}_lastQuestionIndex`;
      localStorage.setItem(localStorageKey, newIndex.toString());
      lastViewedQuestionIndexRef.current = newIndex;
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      const newIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(newIndex);
      const localStorageKey = `exam_${exam.exam_id}_attempt_${attemptId}_lastQuestionIndex`;
      localStorage.setItem(localStorageKey, newIndex.toString());
      lastViewedQuestionIndexRef.current = newIndex;
    }
  };

  const handleNavigateToQuestion = (index) => {
    if (examLocked) return;
    setCurrentQuestionIndex(index);
    const localStorageKey = `exam_${exam.exam_id}_attempt_${attemptId}_lastQuestionIndex`;
    localStorage.setItem(localStorageKey, index.toString());
    lastViewedQuestionIndexRef.current = index;
  };

  const handleAutoSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      await flushAllClassicAnswers();
      const response = await apiFetch(
        `/student/exams/${exam.exam_id}/submit`,
        {
          method: 'POST',
          body: JSON.stringify({
            attempt_id: attemptId,
            auto_save: true,
            answers: {} // Otomatik kaydetme her zaman aktif, cevaplar zaten kaydedilmiş
          })
        }
      );

      if (response.status === 403) {
        setAutoSubmitModal({ message: 'Sınav teslim izni bulunamadı. Lütfen öğrenci hesabınızla tekrar giriş yapın.' });
        return;
      }
      const data = await response.json();
      if (data.success) {
        setAutoSubmitModal({ message: 'Sınav otomatik olarak teslim edildi. Sonuçlar öğretmen tarafından yayınlandığında görüntülenebilir.' });
      } else {
        setAutoSubmitModal({ message: data.message || 'Sınav teslim edilemedi.' });
      }
    } catch (err) {
      console.error('Sınav teslim hatası:', err);
      setAutoSubmitModal({ message: 'Sınav teslim edilirken bir hata oluştu.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    // Finish confirmation varsa göster
    if (finishConfirmation) {
      setCurrentPage('finish-confirmation');
      return;
    }

    // Onay yoksa direkt teslim et
    const confirmSubmit = window.confirm('Sınavı teslim etmek istediğinize emin misiniz? Bu işlem geri alınamaz.');
    if (!confirmSubmit) return;

    await submitExam();
  };
  
  const submitExam = async () => {
    setIsSubmitting(true);

    try {
      await flushAllClassicAnswers();
      const response = await apiFetch(
        `/student/exams/${exam.exam_id}/submit`,
        {
          method: 'POST',
          body: JSON.stringify({
            attempt_id: attemptId,
            auto_save: true,
            answers: {} // Otomatik kaydetme her zaman aktif, cevaplar zaten kaydedilmiş
          })
        }
      );

      if (response.status === 403) {
        alert('Sınav teslim izni bulunamadı. Lütfen öğrenci hesabınızla tekrar giriş yapın.');
        return;
      }
      const data = await response.json();
      if (data.success) {
        setSubmitResult({
          total_points: data.total_points
        });
        setCurrentPage('submit-success');
      } else {
        alert(data.message || 'Sınav teslim edilemedi');
      }
    } catch (err) {
      console.error('Sınav teslim hatası:', err);
      alert('Sınav teslim edilirken hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleConfirmFinish = () => {
    setCurrentPage(null);
    submitExam();
  };

  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return 'Süresiz';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getAnsweredCount = () => {
    return Object.keys(answers).length + Object.keys(classicAnswers).length + skippedQuestions.size;
  };

  if (loading) {
    return (
      <div className="exam-taking-container">
        <div className="loading-state">
          <p>Sorular yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error && !timeExpired) {
    return (
      <div className="exam-taking-container">
        <div className="error-state">
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => onComplete()}>
            Geri Dön
          </button>
        </div>
      </div>
    );
  }

  if (timeExpired) {
    return (
      <div className="exam-taking-container">
        <div className="error-state" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '2rem',
          padding: '3rem 2rem',
          minHeight: '400px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <p style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: '#dc3545',
              margin: '0 0 0.5rem 0',
              lineHeight: '1.6'
            }}>
              {error || 'Süre doldu, sınav otomatik olarak teslim edildi.'}
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => onComplete()}
            style={{
              padding: '1rem 2.5rem',
              fontSize: '1.1rem',
              fontWeight: '600',
              background: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
              minWidth: '200px',
              maxWidth: '300px',
              width: '100%'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#45a049';
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 16px rgba(76, 175, 80, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#4CAF50';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(76, 175, 80, 0.3)';
            }}
          >
            Ana Sayfaya Dön
          </button>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="exam-taking-container">
        <div className="error-state">
          <p>Sınavda soru bulunmamaktadır.</p>
          <button className="btn btn-primary" onClick={() => onComplete()}>
            Geri Dön
          </button>
        </div>
      </div>
    );
  }

  // Finish confirmation sayfası
  if (currentPage === 'finish-confirmation') {
    return (
      <div className="exam-taking-container">
        <div className="dashboard-section" style={{ maxWidth: '600px', margin: '2rem auto', padding: '2rem' }}>
          <h2 style={{ marginBottom: '1.5rem', textAlign: 'center' }}>Sınavı Teslim Et</h2>
          <div style={{ 
            background: '#fff3cd', 
            border: '2px solid #ffc107', 
            borderRadius: '8px', 
            padding: '1.5rem', 
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '1.1rem', margin: 0, lineHeight: '1.6' }}>
              {finishConfirmation || 'Sınavı teslim etmek istediğinize emin misiniz? Bu işlem geri alınamaz.'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              className="btn btn-secondary"
              onClick={() => setCurrentPage(null)}
              style={{
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                fontWeight: '600'
              }}
            >
              İptal
            </button>
            <button
              className="btn btn-primary"
              onClick={handleConfirmFinish}
              disabled={isSubmitting}
              style={{
                background: '#4CAF50',
                color: 'white',
                border: 'none',
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: isSubmitting ? 'not-allowed' : 'pointer'
              }}
            >
              {isSubmitting ? 'Teslim Ediliyor...' : 'Onaylıyorum ve Teslim Ediyorum'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Cevaplanan soru sayısı (boş bırakılan / skip edilen sayılmaz)
  const getAnsweredQuestionCount = () => {
    let count = 0;
    questions.forEach((q) => {
      const id = q.attempt_question_id;
      if (q.question_type === 'Classic') {
        const text = (classicAnswers[id] || '').trim();
        if (text !== '') count++;
      } else {
        const ans = answers[id];
        if (ans && ans !== 'SKIP') count++;
      }
    });
    return count;
  };

  // Submit success sayfası
  if (currentPage === 'submit-success' && submitResult) {
    const answeredCount = getAnsweredQuestionCount();
    const totalCount = questions.length;
    return (
      <div className="exam-taking-container">
        <div className="dashboard-section submit-success-card">
          <h2 className="submit-success-title">Sınav Başarıyla Teslim Edildi</h2>
          <div className="submit-success-not-box">
            <p className="submit-success-not-text">Notunuz daha sonra yayınlanacaktır.</p>
          </div>
          <p className="submit-success-answered">
            {totalCount} sorunun {answeredCount}&apos;si cevaplandı.
          </p>
          <button
            className="btn btn-primary submit-success-btn"
            onClick={() => onComplete()}
          >
            Ana Sayfaya Dön
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = currentQuestion.question_type === 'Classic'
    ? classicAnswers[currentQuestion.attempt_question_id] || ''
    : answers[currentQuestion.attempt_question_id] || '';
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const canProceed = currentQuestion.question_type === 'Classic'
    ? (currentAnswer || '').trim().length > 0
    : Boolean(currentAnswer); // A/B/C/D/E or SKIP
  const navDisabled = examLocked || isSubmitting;
  const nextDisabled = navDisabled || !canProceed;
  const submitDisabled = navDisabled;
  
  return (
    <div
      className={`exam-taking-container${examLocked ? ' exam-locked' : ''}`}
      style={viewportHeight ? { '--exam-visible-height': `${viewportHeight}px` } : undefined}
    >
      {/* Otomatik teslim modalı */}
      {autoSubmitModal && (
        <div className="violation-modal-overlay">
          <div className="violation-modal">
            <div className="violation-modal-icon">✅</div>
            <h2 className="violation-modal-title" style={{ color: '#2e7d32' }}>Sınav Teslim Edildi</h2>
            <p className="violation-modal-message">{autoSubmitModal.message}</p>
            <button
              className="violation-modal-btn"
              style={{ background: '#4CAF50' }}
              onClick={() => { setAutoSubmitModal(null); onComplete(); }}
            >
              Tamam
            </button>
          </div>
        </div>
      )}

      {/* İhlal uyarı modalı */}
      {violationModal && (
        <div className="violation-modal-overlay">
          <div className="violation-modal">
            <div className="violation-modal-icon">⚠️</div>
            <h2 className="violation-modal-title">Uyarı!</h2>
            <p className="violation-modal-message">
              Sınav sırasında sekme değiştirmeniz veya başka bir uygulamaya geçmeniz yasaktır.
            </p>
            <div className="violation-modal-counter">
              {violationModal.count}. uyarı / {violationModal.max} hakkınız var
            </div>
            <br />
            <button
              className="violation-modal-btn"
              onClick={() => {
                setViolationModal(null);
                setExamLocked(false);
                // Fullscreen geçişi sırasında yanlış ihlal sayılmasını önle
                fullscreenTransitionRef.current = true;
                setTimeout(() => { fullscreenTransitionRef.current = false; }, 1500);
                // lastViolationTime'ı güncelle — debounce penceresini uzat
                lastViolationTimeRef.current = Date.now();
                // Tam ekrana geri dön
                const el = document.documentElement;
                if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
                else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
              }}
            >
              Anladım, Tam Ekrana Dön
            </button>
          </div>
        </div>
      )}

      {/* Üst Bölüm: Logo | Başlık + Kalan Zaman | Öğrenci Bilgisi */}
      <div className="exam-taking-header">
        <div className="exam-header-left">
          <img src={logo} alt="Üniversite Logosu" className="exam-logo" />
        </div>
        <div className="exam-header-center">
          <h2>{exam.course_name ? `${exam.course_name} - ${exam.title}` : exam.title}</h2>
          <div className={`timer ${remainingTime !== null && remainingTime < 300 ? 'timer-warning' : ''}`}>
            <span className="timer-label">Kalan Süre:</span>
            <span className="timer-value">{formatTime(remainingTime)}</span>
          </div>
        </div>
        <div className="exam-header-right">
          <div className="student-info-card">
            <div className="student-info-left">
              {user?.student_number && <span className="student-number">{user.student_number}</span>}
              {user?.name && <span className="student-name">{user.name}</span>}
            </div>
            <div className="student-info-right">
              <span className="header-date">{currentDateTime.toLocaleDateString('tr-TR')}</span>
              <span className="header-time">{currentDateTime.toLocaleTimeString('tr-TR')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Ana İçerik: Sol Sidebar + Soru Alanı */}
      <div className="exam-taking-content">
        {/* Sol Sidebar: Soru Indexleri - Sadece bilgi amaçlı, tıklanamaz */}
        <div className="question-navigation">
          <div className="question-navigation-list">
            {questions.map((q, index) => {
              const answer = q.question_type === 'Classic'
                ? (classicAnswers[q.attempt_question_id] || '').trim()
                : answers[q.attempt_question_id];
              const isSkipped = skippedQuestions.has(q.attempt_question_id);
              // SKIP durumunu answered olarak sayma
              const isAnswered = answer && answer !== 'SKIP' && !isSkipped;
              const isCurrent = index === currentQuestionIndex;
              const isFlaggedNav = flaggedQuestions.has(q.attempt_question_id);
              return (
                <div
                  key={q.attempt_question_id}
                  className={`nav-button ${isCurrent ? 'nav-current' : ''} ${isSkipped ? 'nav-skipped' : ''} ${isAnswered ? 'nav-answered' : ''} ${isFlaggedNav ? 'nav-flagged' : ''}`}
                  style={{ cursor: examLocked ? 'not-allowed' : 'pointer' }}
                  onClick={() => handleNavigateToQuestion(index)}
                  title={isSkipped ? 'Geçildi' : isFlaggedNav ? 'İşaretlendi' : isAnswered ? 'Cevaplandı' : 'Cevaplanmadı'}
                >
                  {index + 1}
                </div>
              );
            })}
          </div>
        </div>

        {/* Sağ Bölüm: Soru ve Cevaplar */}
        <div className="question-container">
          <div className="question-header">
            <h3>
              Soru {currentQuestionIndex + 1} / {questions.length}
            </h3>
          </div>

          {currentQuestion.image_url && (() => {
            const src = resolveMediaUrl(currentQuestion.image_url);
            return (
              <div className="question-image" style={{ margin: '12px 0', textAlign: 'center' }}>
                <img
                  src={src}
                  alt="Soru görseli"
                  style={{ maxWidth: '100%', maxHeight: 420, borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'zoom-in' }}
                  onClick={() => window.open(src, '_blank')}
                />
              </div>
            );
          })()}

          <div className="question-text">
            <MathText text={currentQuestion.question_text} />
          </div>

          {currentQuestion.question_type === 'Classic' ? (
            <div className="question-classic-answer">
              <textarea
                className="classic-answer-textarea"
                value={currentAnswer || ''}
                onChange={(e) => handleClassicAnswerChange(currentQuestion.attempt_question_id, e.target.value)}
                onBlur={() => flushClassicAnswer(currentQuestion.attempt_question_id)}
                placeholder="Cevabınızı buraya yazın..."
                rows={8}
                disabled={examLocked}
              />
            </div>
          ) : (
            <div className="question-options">
              {['A', 'B', 'C', 'D', 'E'].map((option) => {
                const optionText = currentQuestion[`option_${option.toLowerCase()}`];
                // Sadece mevcut şıkları göster, boş olanları gösterme
                if (!optionText) return null;

                const isSelected = currentAnswer === option;
                return (
                  <label
                    key={option}
                    className={`option-label ${isSelected ? 'option-selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name={`question-${currentQuestion.attempt_question_id}`}
                      value={option}
                      checked={isSelected}
                      onChange={() => handleAnswerChange(currentQuestion.attempt_question_id, option)}
                      disabled={examLocked}
                    />
                    <span className="option-letter">{option}:</span>
                    <span className="option-text"><MathText text={optionText} /></span>
                  </label>
                );
              })}
              {/* Yalancı şık: Boş bırakmak istiyorum - Her zaman son şıktan sonra gelir */}
              <label
                className={`option-label ${currentAnswer === 'SKIP' ? 'option-selected' : ''}`}
              >
                <input
                  type="radio"
                  name={`question-${currentQuestion.attempt_question_id}`}
                  value="SKIP"
                  checked={currentAnswer === 'SKIP'}
                  onChange={() => handleAnswerChange(currentQuestion.attempt_question_id, 'SKIP')}
                  disabled={examLocked}
                />
                <span className="option-text" style={{ marginLeft: '0', fontWeight: '500', color: '#ff9800' }}>Boş bırakmak istiyorum</span>
              </label>
            </div>
          )}

        </div>
      </div>

      {/* Tek aksiyon barı: her boyutta sabit erişim */}
      <div className="exam-taking-footer">
        <div className="exam-taking-footer-actions">
          <div className="exam-footer-progress">
            Soru {currentQuestionIndex + 1} / {questions.length}
          </div>
          <div className="exam-nav-buttons exam-footer-nav" role="group" aria-label="Soru navigasyonu">
            {currentQuestionIndex > 0 && (
              <button
                type="button"
                className="btn btn-prev exam-geri-btn"
                onClick={handlePreviousQuestion}
                disabled={navDisabled}
              >
                ← Önceki Soru
              </button>
            )}
            {!isLastQuestion ? (
              <button
                type="button"
                className="btn btn-next exam-ileri-btn"
                onClick={handleNextQuestion}
                disabled={nextDisabled}
              >
                İleri →
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-submit"
                onClick={handleSubmit}
                disabled={submitDisabled}
              >
                {isSubmitting ? 'Teslim Ediliyor...' : 'Sınavı Teslim Et'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StudentExamTaking;
